/**
 * Where the week's milk went (BR-23).
 *
 * The farm wrote milk down three times — collected per cow, delivered on the
 * round, sold at the gate — and never against each other. Litres could leave
 * the shed and arrive nowhere with nothing to notice it. Feed has had this
 * check since P2 (BR-21); milk did not.
 *
 * All milk is sold on this farm: no calf ration, no household use, no accepted
 * wastage. That is why a shortfall is named as missing rather than shown as a
 * neutral "other" — there is no honest category for it to land in, and the
 * whole value of the report is that it prompts the question on the day rather
 * than at the monthly close.
 *
 * A clean day is stated as clean rather than left blank. Silence reads as "not
 * checked"; the point is that it was checked and it added up.
 */

import { useTranslation } from '../../i18n/useTranslation.js';
import { formatDate } from '../../lib/date.js';
import { useMilkBalance, type MilkDay } from './api.js';

function DayRow({ day }: { day: MilkDay }): JSX.Element {
  const { t } = useTranslation();

  // Nothing happened at all — worth a quieter row than a day that balanced.
  const empty = day.collected === '0.00' && day.accountedFor === '0.00';
  const short = !day.isClean && !day.difference.startsWith('-');

  return (
    <li className="flex items-baseline justify-between gap-3 border-b border-gray-100 py-2 last:border-b-0">
      <div className="min-w-0">
        <p className="text-sm text-gray-800">{formatDate(day.date)}</p>
        <p className="text-xs text-gray-500">
          {t('milk.balance.breakdown', {
            collected: day.collected,
            delivered: day.delivered,
            sold: day.soldAtGate,
          })}
        </p>
      </div>

      <span
        className={`shrink-0 text-sm tabular-nums font-medium ${
          empty ? 'text-gray-400' : day.isClean ? 'text-green-700' : short ? 'text-red-700' : 'text-amber-800'
        }`}
      >
        {empty
          ? '—'
          : day.isClean
            ? t('milk.balance.clean')
            : short
              ? t('milk.balance.short', { litres: day.difference })
              : // More sold than collected: almost always a milking session
                // nobody entered, not milk from nowhere.
                t('milk.balance.overSold', { litres: day.difference.replace('-', '') })}
      </span>
    </li>
  );
}

export function MilkBalancePanel({ from, to }: { from: string; to: string }): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useMilkBalance(from, to);

  const problems = data?.days.filter((day) => !day.isClean) ?? [];

  return (
    <section className="mb-5 rounded-xl bg-white p-4 shadow-sm" aria-label={t('milk.balance.title')}>
      <h2 className="mb-1 text-base font-semibold text-gray-800">{t('milk.balance.title')}</h2>
      <p className="mb-3 text-xs text-gray-500">{t('milk.balance.subtitle')}</p>

      {isLoading && (
        <p role="status" className="py-3 text-center text-sm text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          <p className="mb-1">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      {/* The headline, so the week can be read without scanning seven rows. */}
      {problems.length > 0 && (
        <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          {t('milk.balance.unexplained', { count: problems.length })}
        </p>
      )}

      {data && problems.length === 0 && data.totals.collected !== '0.00' && (
        <p className="mb-3 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">{t('milk.balance.allClean')}</p>
      )}

      {data && (
        <ul>
          {data.days.map((day) => (
            <DayRow key={day.date} day={day} />
          ))}
        </ul>
      )}
    </section>
  );
}
